import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
} from '../components/icons';
import { TimePickerInput } from '../components/TimePickerInput';
import {
  buildMonthGrid,
  cleanHolidaysForApi,
  cleanWeeklyScheduleForApi,
  cleanReminderSettingsForApi,
  formatHolidayDisplayDate,
  formatMonthTitle,
  getHolidayReason,
  getHolidayTypeLabel,
  getValidHolidays,
  isValidHoliday,
  normalizeHolidays,
  normalizeWeeklySchedule,
  normalizeReminderSettings,
  validateReminderSettings,
  timeToMinutes,
  toDayKey,
  validateSettingsForm,
  addMonthsToKey,
} from '../utils';
import { useAppointmentSettings, useUpdateAppointmentSettings } from '../hooks';
import { Button } from '@/components';
import { parseApiError } from '@/common/utils';
import { DashboardPage } from '../../components/PremiumDesign';
import { CustomiseDayTable } from '../components/CustomiseDayTable';
import { AppointmentReminderSettings } from '../components/AppointmentReminderSettings';

const SETTINGS_DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function buildAvailabilitySnapshot(settings) {
  return {
    weekly_schedule: normalizeWeeklySchedule(settings?.weekly_schedule),
    holidays: getValidHolidays(normalizeHolidays(settings?.holidays)),
  };
}

function purgeInvalidHolidays(holidays, keepDate = null) {
  return (holidays || []).filter(
    (h) => h.date === keepDate || isValidHoliday(h),
  );
}

function getDefaultHoursFromWeekly(weekly) {
  const openDay = SETTINGS_DAY_ORDER.find((d) => weekly[d]?.type !== 'closed');
  if (!openDay) {
    return { start: '09:00', end: '18:00', activeDay: 'monday' };
  }
  return {
    start: weekly[openDay].working_hours_start || '09:00',
    end: weekly[openDay].working_hours_end || '18:00',
    activeDay: openDay,
  };
}

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch {
    // ignore
  }
  return localStorage.getItem('company_id');
}

export function BookingAvailabilityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = getCompanyId();
  const { data: settings } = useAppointmentSettings(companyId);
  const mutation = useUpdateAppointmentSettings(companyId);

  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'availability';
  const setActiveTab = (tab) => navigate(`?tab=${tab}`, { replace: true });

  const [draftReminders, setDraftReminders] = useState([]);

  const [draftAvailability, setDraftAvailability] = useState(() =>
    buildAvailabilitySnapshot(null),
  );

  const [defaultStart, setDefaultStart] = useState('09:00');
  const [defaultEnd, setDefaultEnd] = useState('18:00');
  const [breakStart, setBreakStart] = useState('13:00');
  const [breakEnd, setBreakEnd] = useState('14:00');

  const [hoursError, setHoursError] = useState('');

  const [isCustomiseOpen, setIsCustomiseOpen] = useState(false);

  const [holidayMonthKey, setHolidayMonthKey] = useState(() =>
    toDayKey(new Date()),
  );
  const [selectedHolidayDate, setSelectedHolidayDate] = useState(null);

  const syncFromSettings = (nextSettings) => {
    if (!nextSettings) return;
    const snapshot = buildAvailabilitySnapshot(nextSettings);
    const cloned = deepClone(snapshot);
    setDraftAvailability(cloned);
    setDraftReminders(
      normalizeReminderSettings(nextSettings?.reminder_settings),
    );
    const defaults = getDefaultHoursFromWeekly(cloned.weekly_schedule);
    setDefaultStart(defaults.start);
    setDefaultEnd(defaults.end);
    setHoursError('');
  };

  useEffect(() => {
    if (settings) {
      syncFromSettings(settings);
    }
  }, [settings]);

  const draftWeekly = draftAvailability.weekly_schedule;
  const draftHolidays = draftAvailability.holidays;
  const validHolidays = useMemo(
    () => getValidHolidays(draftHolidays),
    [draftHolidays],
  );

  const openCount = SETTINGS_DAY_ORDER.filter(
    (d) => draftWeekly[d]?.type !== 'closed',
  ).length;
  const closedCount = 7 - openCount;
  const holidayCount = validHolidays.filter(
    (h) => h.type === 'full_day_off',
  ).length;
  const halfDayCount = validHolidays.filter(
    (h) => h.type === 'half_day',
  ).length;

  const patchDraftWeekly = (updater) => {
    setDraftAvailability((prev) => ({
      ...prev,
      weekly_schedule:
        typeof updater === 'function' ? updater(prev.weekly_schedule) : updater,
    }));
  };

  const patchDraftHolidays = (updater) => {
    setDraftAvailability((prev) => ({
      ...prev,
      holidays:
        typeof updater === 'function' ? updater(prev.holidays) : updater,
    }));
  };

  const isEndAfterStart = (start, end) => {
    const startMins = timeToMinutes(start);
    const endMins = timeToMinutes(end);
    return startMins != null && endMins != null && endMins > startMins;
  };

  const handleDefaultStartChange = (v) => {
    if (!isEndAfterStart(v, defaultEnd)) {
      setHoursError('Closes time must be after Opens time');
      return;
    }
    setHoursError('');
    setDefaultStart(v);
    // Update all open days
    patchDraftWeekly((prev) => {
      const next = { ...prev };
      for (const day of SETTINGS_DAY_ORDER) {
        if (next[day]?.type !== 'closed') {
          next[day] = { ...next[day], working_hours_start: v };
        }
      }
      return next;
    });
  };

  const handleDefaultEndChange = (v) => {
    if (!isEndAfterStart(defaultStart, v)) {
      setHoursError('Closes time must be after Opens time');
      return;
    }
    setHoursError('');
    setDefaultEnd(v);
    patchDraftWeekly((prev) => {
      const next = { ...prev };
      for (const day of SETTINGS_DAY_ORDER) {
        if (next[day]?.type !== 'closed') {
          next[day] = { ...next[day], working_hours_end: v };
        }
      }
      return next;
    });
  };

  const toggleDayOpen = (day) => {
    patchDraftWeekly((prev) => {
      const wasClosed = prev[day]?.type === 'closed';
      return {
        ...prev,
        [day]: wasClosed
          ? {
              type: 'working',
              working_hours_start: defaultStart,
              working_hours_end: defaultEnd,
            }
          : {
              type: 'closed',
              working_hours_start: null,
              working_hours_end: null,
            },
      };
    });
  };

  const holidayByDate = useMemo(() => {
    const map = {};
    for (const h of draftHolidays) {
      if (h.date) map[h.date] = h;
    }
    return map;
  }, [draftHolidays]);

  const validHolidayByDate = useMemo(() => {
    const map = {};
    for (const h of validHolidays) {
      if (h.date) map[h.date] = h;
    }
    return map;
  }, [validHolidays]);

  const sortedValidHolidays = useMemo(
    () => [...validHolidays].sort((a, b) => a.date.localeCompare(b.date)),
    [validHolidays],
  );

  const monthCells = useMemo(
    () => buildMonthGrid(holidayMonthKey),
    [holidayMonthKey],
  );

  const removeHolidayByDate = (dayKey) => {
    patchDraftHolidays((prev) => prev.filter((h) => h.date !== dayKey));
    if (selectedHolidayDate === dayKey) {
      setSelectedHolidayDate(null);
    }
  };

  const toggleHoliday = (dayKey) => {
    const existing = holidayByDate[dayKey];
    if (existing) {
      if (!isValidHoliday(existing)) {
        removeHolidayByDate(dayKey);
        toast.error('Holiday removed — title/reason is required');
        return;
      }
      patchDraftHolidays((prev) => purgeInvalidHolidays(prev, dayKey));
      setSelectedHolidayDate(dayKey);
      return;
    }

    patchDraftHolidays((prev) => {
      const cleaned = purgeInvalidHolidays(prev, dayKey);
      return [
        ...cleaned.filter((h) => h.date !== dayKey),
        {
          date: dayKey,
          name: '',
          reason: '',
          type: 'full_day_off',
          working_hours_start: null,
          working_hours_end: null,
        },
      ];
    });
    setSelectedHolidayDate(dayKey);
  };

  const removeSelectedHoliday = () => {
    if (!selectedHolidayDate) return;
    removeHolidayByDate(selectedHolidayDate);
  };

  const updateSelectedHolidayReason = (reason) => {
    if (!selectedHolidayDate) return;
    const trimmed = reason;
    patchDraftHolidays((prev) =>
      prev.map((h) =>
        h.date === selectedHolidayDate
          ? { ...h, name: trimmed, reason: trimmed }
          : h,
      ),
    );
  };

  const updateHoliday = (date, config) => {
    const reason = getHolidayReason(config);
    patchDraftHolidays((prev) => {
      const existing = prev.find((h) => h.date === date);
      if (!existing) {
        return [
          ...prev,
          {
            date,
            name: reason,
            reason,
            type: config.type || 'full_day_off',
            working_hours_start: config.working_hours_start || '09:00',
            working_hours_end: config.working_hours_end || '18:00',
          },
        ];
      }
      return prev.map((h) =>
        h.date === date
          ? {
              ...h,
              ...config,
              name: reason || h.name,
              reason: reason || h.reason,
            }
          : h,
      );
    });
  };

  const handleSave = async () => {
    const holidaysToSave = getValidHolidays(draftHolidays);
    const errors = validateSettingsForm(draftWeekly, holidaysToSave);
    if (Object.keys(errors).length) {
      setActiveTab('availability');
      toast.error(Object.values(errors)[0] || 'Invalid settings');
      return;
    }

    const reminderError = validateReminderSettings(draftReminders);
    if (reminderError) {
      setActiveTab('reminders');
      toast.error(reminderError);
      return;
    }

    try {
      await mutation.mutateAsync({
        weekly_schedule: cleanWeeklyScheduleForApi(draftWeekly),
        holidays: cleanHolidaysForApi(holidaysToSave),
        reminder_settings: cleanReminderSettingsForApi(draftReminders),
      });

      setDraftAvailability((prev) => ({
        ...prev,
        holidays: holidaysToSave,
      }));

      toast.success('Booking settings saved successfully');
      navigate('/calendar');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to save settings');
    }
  };

  return (
    <DashboardPage className="!p-0 sm:!p-0 !bg-transparent">
      <div
        className={`booking-settings bg-white rounded-2xl flex flex-col overflow-hidden border border-[#EBF4EA] ${
          activeTab === 'reminders' ? 'h-auto max-h-full' : 'flex-1'
        }`}
      >
        {/* Header with Tabs */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border-default/40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/calendar')}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-border-default bg-white text-text-secondary hover:text-text-primary hover:border-primary transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-text-primary">
              {activeTab === 'reminders'
                ? 'Appointment Reminders'
                : 'Booking Settings'}
            </h1>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-1 bg-surface-mist p-1 rounded-xl self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setActiveTab('availability')}
              className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'availability'
                  ? 'bg-white text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Working Hours & Holidays
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('reminders')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                activeTab === 'reminders'
                  ? 'bg-white text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <span>Appointment Reminders</span>
              {draftReminders.length > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold">
                  {draftReminders.filter((r) => r.is_enabled !== false).length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div
          className={`px-6 ${
            activeTab === 'reminders'
              ? 'pt-4 pb-4 min-h-0 overflow-y-auto'
              : 'flex-1 py-6 pb-6 overflow-auto'
          }`}
        >
          {activeTab === 'availability' ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 items-start">
              {/* Left Column Wrapper */}
              <div className="flex flex-col gap-6">
                {/* Stats Row */}
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="flex items-center justify-between p-4 rounded-xl border border-primary/40 bg-primary/10">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-primary" />
                      <span className="text-base font-medium text-text-primary">
                        Working Day
                      </span>
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      {String(openCount).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl border border-status-error/40 bg-status-error/10">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-status-error" />
                      <span className="text-base font-medium text-text-primary">
                        Closed
                      </span>
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      {String(closedCount).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl border border-[#F1A132]/40 bg-[#F1A132]/10">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-[#F1A132]" />
                      <span className="text-base font-medium text-text-primary">
                        Holiday
                      </span>
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      {String(holidayCount).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl border border-[#B224EF]/40 bg-[#B224EF]/10">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-[#B224EF]" />
                      <span className="text-base font-medium text-text-primary">
                        Half Day
                      </span>
                    </div>
                    <span className="text-base font-bold text-text-primary">
                      {String(halfDayCount).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                {/* Left Column Form */}
                <div className="bg-form-background-light rounded-2xl p-4 flex flex-col">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-base text-text-primary mb-4">
                        Working Hours
                      </h3>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="text-xs font-medium text-text-secondary mb-1">
                            Opens
                          </div>
                          <TimePickerInput
                            value={defaultStart}
                            onChange={handleDefaultStartChange}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-medium text-text-secondary mb-1">
                            Closes
                          </div>
                          <TimePickerInput
                            value={defaultEnd}
                            onChange={handleDefaultEndChange}
                          />
                        </div>
                      </div>
                      {hoursError && (
                        <p className="mt-2 text-xs text-red-500">
                          {hoursError}
                        </p>
                      )}
                    </div>

                    <div>
                      <h3 className="text-base text-text-primary mb-4">
                        Break Hours
                      </h3>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="text-xs font-medium text-text-secondary mb-1">
                            Opens
                          </div>
                          <TimePickerInput
                            value={breakStart}
                            onChange={setBreakStart}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-medium text-text-secondary mb-1">
                            Closes
                          </div>
                          <TimePickerInput
                            value={breakEnd}
                            onChange={setBreakEnd}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-base text-text-primary mb-4">
                      Select your working days
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {SETTINGS_DAY_ORDER.map((day) => {
                        const isWorking = draftWeekly[day]?.type !== 'closed';
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDayOpen(day)}
                            className={`flex items-center gap-2 h-10 px-4 rounded-xl border text-sm font-medium transition-colors ${
                              isWorking
                                ? 'bg-white border-primary text-primary'
                                : 'bg-white border-status-error text-status-error'
                            }`}
                          >
                            {isWorking && (
                              <div className="flex items-center justify-center w-4 h-4 rounded bg-primary text-white">
                                <CheckIcon className="w-3 h-3" />
                              </div>
                            )}
                            {!isWorking && (
                              <div className="flex items-center justify-center w-4 h-4 rounded border border-status-error" />
                            )}
                            <span className="capitalize">{day}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-col p-4 rounded-xl bg-white border border-border-default overflow-hidden">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-medium text-text-primary">
                        Customise Working Hours For A Specific Day
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsCustomiseOpen(!isCustomiseOpen)}
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          isCustomiseOpen ? 'bg-primary' : 'bg-border-default'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                            isCustomiseOpen ? 'left-[18px]' : 'left-1'
                          }`}
                        />
                      </button>
                    </div>

                    {isCustomiseOpen && (
                      <CustomiseDayTable
                        weeklySchedule={draftWeekly}
                        onUpdateWeekly={(dayKey, newConfig) => {
                          patchDraftWeekly((prev) => ({
                            ...prev,
                            [dayKey]: newConfig,
                          }));
                        }}
                        holidays={draftHolidays}
                        onUpdateHoliday={updateHoliday}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column (Calendar) */}
              <div className="bg-form-background-light rounded-2xl p-4 flex flex-col">
                <h3 className="text-lg text-text-primary mb-4">
                  Mark a Holiday
                </h3>

                {/* Legend Card */}
                <div className="bg-white rounded-xl p-4 mb-4">
                  <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-status-error" />
                      <span className="text-sm text-text-primary">
                        Closed / Weekly Off
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#F1A132]" />
                      <span className="text-sm text-text-primary">Holiday</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#B224EF]" />
                      <span className="text-sm text-text-primary">
                        Half Day
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                      <span className="text-sm text-text-primary">
                        Working Day
                      </span>
                    </div>
                  </div>
                </div>

                {/* Calendar Card */}
                <div className="flex-1 bg-white rounded-xl p-5 mb-4">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-base font-semibold text-text-primary">
                      {formatMonthTitle(holidayMonthKey)}
                    </h4>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() =>
                          setHolidayMonthKey((k) => addMonthsToKey(k, -1))
                        }
                        className="flex items-center justify-center text-primary hover:opacity-80 transition-opacity"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() =>
                          setHolidayMonthKey((k) => addMonthsToKey(k, 1))
                        }
                        className="flex items-center justify-center text-primary hover:opacity-80 transition-opacity"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-y-3 gap-x-2 mb-3">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div
                        key={i}
                        className="text-center text-sm font-medium text-primary"
                      >
                        {d}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-y-3 gap-x-2">
                    {monthCells.map((cell, idx) => {
                      if (!cell.inMonth) {
                        return <div key={idx} className="h-8" />;
                      }

                      const dayName = cell.weekdayKey;
                      const isWeeklyOff =
                        draftWeekly[dayName]?.type === 'closed';
                      const holiday = validHolidayByDate[cell.key];
                      const isSelected = selectedHolidayDate === cell.key;
                      const isDraft =
                        isSelected &&
                        holidayByDate[cell.key] &&
                        !isValidHoliday(holidayByDate[cell.key]);

                      let cellClass =
                        'aspect-square flex items-center justify-center text-sm rounded-lg cursor-pointer transition-colors font-medium ';
                      let textColor = 'text-text-primary';

                      if (isWeeklyOff) {
                        cellClass += 'bg-[#FDECEC]';
                        textColor = 'text-status-error';
                      } else if (holiday) {
                        if (holiday.type === 'half_day') {
                          cellClass += 'bg-[#F7E6FE]';
                          textColor = 'text-[#B224EF]';
                        } else {
                          cellClass += 'bg-[#FFF6EC] border border-[#F1A132]';
                          textColor = 'text-[#F1A132]';
                        }
                      } else if (isDraft) {
                        cellClass +=
                          'bg-white border-2 border-dashed border-primary';
                        textColor = 'text-primary';
                      } else {
                        cellClass += 'hover:bg-surface-mist';
                      }

                      if (isSelected && !isWeeklyOff) {
                        cellClass += ' ring-2 ring-primary ring-offset-1';
                      }

                      return (
                        <div
                          key={idx}
                          onClick={() =>
                            !isWeeklyOff && toggleHoliday(cell.key)
                          }
                          className={`${cellClass} ${textColor}`}
                        >
                          {cell.day}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="p-4 border border-dashed border-primary rounded-xl mb-4">
                  {selectedHolidayDate ? (
                    <div className="flex flex-col gap-3 text-left">
                      <div className="text-sm font-medium text-primary">
                        Holiday: {formatHolidayDisplayDate(selectedHolidayDate)}
                      </div>
                      {!isValidHoliday(holidayByDate[selectedHolidayDate]) && (
                        <p className="text-xs text-status-error">
                          Title/reason required. Holiday stays inactive until
                          filled.
                        </p>
                      )}
                      <div>
                        <label
                          htmlFor="holiday-reason"
                          className="block text-xs font-medium text-text-secondary mb-1"
                        >
                          Reason / Title
                        </label>
                        <input
                          id="holiday-reason"
                          type="text"
                          value={holidayByDate[selectedHolidayDate]?.name || ''}
                          onChange={(e) =>
                            updateSelectedHolidayReason(e.target.value)
                          }
                          placeholder="e.g. Independence Day"
                          className="w-full h-10 px-3 rounded-lg border border-border-default bg-white text-sm text-text-primary placeholder:text-text-secondary/60 focus:outline-none focus:border-primary"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeSelectedHoliday}
                        className="text-sm font-medium text-status-error hover:opacity-80 self-start"
                      >
                        Remove holiday
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-primary text-center">
                      No date selected — tap the calendar to add a holiday
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-text-primary mb-3">
                    Holiday List
                  </h4>
                  {sortedValidHolidays.length === 0 ? (
                    <p className="text-sm text-text-secondary text-center py-2">
                      No holidays added yet
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {sortedValidHolidays.map((holiday) => {
                        const isActive = selectedHolidayDate === holiday.date;
                        return (
                          <li key={holiday.date}>
                            <button
                              type="button"
                              onClick={() => {
                                patchDraftHolidays((prev) =>
                                  purgeInvalidHolidays(prev, holiday.date),
                                );
                                setSelectedHolidayDate(holiday.date);
                              }}
                              className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                                isActive
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border-default hover:border-primary/60'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-text-primary truncate">
                                    {getHolidayReason(holiday)}
                                  </div>
                                  <div className="text-xs text-text-secondary mt-0.5">
                                    {formatHolidayDisplayDate(holiday.date)}
                                  </div>
                                </div>
                                <span
                                  className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                    holiday.type === 'half_day'
                                      ? 'bg-[#F7E6FE] text-[#B224EF]'
                                      : holiday.type === 'custom_hours' ||
                                          holiday.type === 'extra_working_day'
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-[#FFF6EC] text-[#F1A132]'
                                  }`}
                                >
                                  {getHolidayTypeLabel(holiday.type)}
                                </span>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-full min-h-0">
              <AppointmentReminderSettings
                reminders={draftReminders}
                onChange={setDraftReminders}
                companyId={companyId}
              />
            </div>
          )}
        </div>

        {/* Footer / Save */}
        <div className="px-6 py-3 bg-white flex justify-end shrink-0 border-t border-border-default/30">
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save →'}
          </Button>
        </div>
      </div>
    </DashboardPage>
  );
}
